import React from 'react';

import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { asFormRankingField } from '../../../../../../../../common/types/form/form-fields/utils';
import type { FormFieldComponentRendererProps } from '../../../../../../../../common/types/renderers';
import { ScrollContainer } from '../../../../../../../../common/ui/scroll-container';
import { getScaleLabelsFromOptions } from '../../../../../../../../common/utils/scale/utils';
import { useFormField } from '../../../../../../../../controllers/form-renderer-store';

const styles = cssMap({
	buttonWrapper: {
		flex: 1,
	},
	labelsRow: {
		marginTop: token('space.150'),
	},
	edgeLabelStack: {
		flex: 1,
		position: 'relative',
	},
	edgeLabelText: {
		width: 'max-content',
		maxWidth: '50%',
		position: 'absolute',
		bottom: token('space.negative.300'),
		whiteSpace: 'normal',
	},
	startLabelText: {
		left: 0,
		textAlign: 'start',
	},
	endLabelText: {
		right: 0,
		textAlign: 'end',
	},
	gatedLabelSpacer: {
		height: '1.5rem',
	},
	container: {
		width: 'fit-content',
		minWidth: '100%',
	},
});

export const PreviewScaleInput = ({
	fieldId,
}: FormFieldComponentRendererProps): React.JSX.Element => {
	di(useFormField);

	const isRatingScaleA11yFixesEnabled = fg('proforma_rating_scale_a11y_fixes');
	const formField = useFormField({ fieldId });
	const formRankingField = asFormRankingField(formField);

	if (formRankingField === undefined) {
		return <></>;
	}

	const selectedOptionId =
		formRankingField.value?.number !== undefined ? String(formRankingField.value.number) : null;
	const { leftLabel, middleLabel, rightLabel } = getScaleLabelsFromOptions(
		formRankingField.options,
	);
	const hasLabels = Boolean(leftLabel && rightLabel);

	if (!isRatingScaleA11yFixesEnabled) {
		return (
			<ScrollContainer>
				<Box testId="field-input-scale-preview" xcss={styles.container}>
					<Inline space="space.200">
						{formRankingField.options.map((option) => {
							const isSelected = option.id === selectedOptionId;
							const inputId = `${fieldId}-${option.id}`;
							return (
								<React.Fragment key={option.id}>
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={option.id}
											label={option.id}
											name={fieldId}
											isChecked={isSelected}
											isDisabled
										/>
									</VisuallyHidden>
									<Box xcss={styles.buttonWrapper}>
										<Button
											appearance={isSelected ? 'primary' : 'default'}
											shouldFitContainer
											isDisabled
											aria-hidden
											tabIndex={-1}
											testId={`field-input-scale-preview-option-${option.id}-${fieldId}`}
										>
											{option.id}
										</Button>
									</Box>
								</React.Fragment>
							);
						})}
					</Inline>
					{hasLabels && (
						<Inline
							spread="space-between"
							alignBlock="center"
							xcss={styles.labelsRow}
							testId="field-input-scale-preview-labels"
						>
							<Text
								size="small"
								color="color.text.subtle"
								testId="field-input-scale-preview-label-left"
							>
								{leftLabel}
							</Text>
							{middleLabel && (
								<Text
									size="small"
									color="color.text.subtle"
									testId="field-input-scale-preview-label-middle"
								>
									{middleLabel}
								</Text>
							)}
							<Text
								size="small"
								color="color.text.subtle"
								testId="field-input-scale-preview-label-right"
							>
								{rightLabel}
							</Text>
						</Inline>
					)}
				</Box>
			</ScrollContainer>
		);
	}

	return (
		<ScrollContainer>
			<Box testId="field-input-scale-preview" xcss={styles.container}>
				<Box xcss={styles.edgeLabelStack}>
					<Inline
						space="space.200"
						alignBlock="start"
						role="radiogroup"
						aria-label={formRankingField.label}
					>
						{formRankingField.options.map((option, index) => {
							const isSelected = option.id === selectedOptionId;
							const inputId = `${fieldId}-${option.id}`;
							return (
								<Box key={option.id} xcss={styles.buttonWrapper}>
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={option.id}
											label={option.id}
											name={fieldId}
											isChecked={isSelected}
											isDisabled
										/>
									</VisuallyHidden>
									<Button
										appearance={isSelected ? 'primary' : 'default'}
										shouldFitContainer
										isDisabled
										aria-hidden
										tabIndex={-1}
										testId={`field-input-scale-preview-option-${option.id}-${fieldId}`}
									>
										{option.id}
									</Button>
									{index === 0 && hasLabels && (
										<Box
											xcss={cx(styles.edgeLabelText, styles.startLabelText)}
											testId="field-input-scale-preview-label-left"
										>
											<Text size="small" color="color.text.subtle">
												{leftLabel}
											</Text>
										</Box>
									)}
									{index === formRankingField.options.length - 1 && hasLabels && (
										<Box
											xcss={cx(styles.edgeLabelText, styles.endLabelText)}
											testId="field-input-scale-preview-label-right"
										>
											<Text size="small" color="color.text.subtle">
												{rightLabel}
											</Text>
										</Box>
									)}
								</Box>
							);
						})}
					</Inline>
				</Box>
				{hasLabels && <Box xcss={styles.gatedLabelSpacer} />}
			</Box>
		</ScrollContainer>
	);
};
